import React, {useCallback, useEffect, useRef, useState} from 'react';
import BaseModal from '@/components/modals/BaseModal';
import Button from '@/components/ui/Button';
import {useStoreActions, useStoreState} from 'store';
import i18n from 'i18n';
import QueryHelpNav from './QueryHelpNav';
import {QueryHelpPageId} from './content/types';
import SimpleSamplesPage from './pages/SimpleSamplesPage';
import SyntaxReferencePage from './pages/SyntaxReferencePage';
import QuerySamplesPage from './pages/QuerySamplesPage';
import ComplexSamplesPage from './pages/ComplexSamplesPage';
import ProductionPatternsPage from './pages/ProductionPatternsPage';
import SalesExamplesPage from './pages/SalesExamplesPage';

interface Props {
    isOpen?: boolean;
    close?: () => void;
}

const DEFAULT_PAGE: QueryHelpPageId = 'simple';

const PAGE_COMPONENTS: Record<QueryHelpPageId, React.FC> = {
    simple: SimpleSamplesPage,
    syntax: SyntaxReferencePage,
    samples: QuerySamplesPage,
    complex: ComplexSamplesPage,
    production: ProductionPatternsPage,
    sales: SalesExamplesPage,
};

const QueryHelpModal = React.memo<Props>(({
    isOpen = false,
    close = () => { },
}) => {
    const storeOpen = useStoreState((state) => state.queryHelpModal.queryHelpModalOpen);
    const setHelpModal = useStoreActions((actions) => actions.queryHelpModal.setQueryHelpModalOpen);
    const [activePage, setActivePage] = useState<QueryHelpPageId>(DEFAULT_PAGE);
    const contentRef = useRef<HTMLDivElement>(null);

    const modalOpen = storeOpen || isOpen;

    useEffect(() => {
        if (modalOpen) {
            setActivePage(DEFAULT_PAGE);
        }
    }, [modalOpen]);

    const closeModal = useCallback(() => {
        close();
        setHelpModal(false);
    }, [close, setHelpModal]);

    const handleSelectPage = useCallback((page: QueryHelpPageId) => {
        setActivePage(page);
        contentRef.current?.scrollTo({top: 0});
    }, []);

    const ActivePage = PAGE_COMPONENTS[activePage];

    return (
        <BaseModal
            isOpen={modalOpen}
            onClose={closeModal}
            title="How to write queries"
            size="xl"
            centered
            backdrop="static"
            backdropClassName="query-help-modal-backdrop"
            className="query-help-modal"
            dialogClassName="query-help-modal-dialog"
            footer={(
                <Button variant="secondary" onClick={closeModal} data-cy="info-undefined-return-btn">
                    {i18n.t('jt_close.text') || 'Close'}
                </Button>
            )}
        >
            <div className="query-help-modal__layout">
                <QueryHelpNav activePage={activePage} onSelect={handleSelectPage} />
                <div ref={contentRef} className="query-help-modal__content">
                    <ActivePage />
                </div>
            </div>
        </BaseModal>
    );
});

export default QueryHelpModal;
